// ── LT-2 — the billing data layer ────────────────────────────────────────────
// Three calls: read the state, start a checkout, open the portal. The two
// Stripe calls go through edge functions because the secret key must never be
// in a browser bundle.
//
// ⛔ NOTHING HERE IS A SECURITY CONTROL. The read below is a convenience for
// the UI; the real gate is can_write() in the database, which every write in
// the app passes through whatever this file says. If this function returned
// "active" for a lapsed company, the banner would be wrong and the writes
// would still be refused — which is the correct way round.
import { supabase } from "./supabaseClient";
import { getMyMembership } from "./auth";

function client() {
  if (!supabase) throw new Error("Supabase is not configured (check .env).");
  return supabase;
}

/** Reasons an edge function can refuse, in the sole-trader voice. */
export function billingReasonMessage(reason) {
  switch (reason) {
    case "mock":
      return "Card payments are not switched on yet. Nothing has been charged — try again shortly, or email us and we will sort it out.";
    case "not_owner":
      return "Only the owner of the business can change the subscription.";
    case "no_company":
      return "We could not find your business. Sign out and back in, then try again.";
    case "no_customer":
      return "There is no subscription to manage yet. Subscribe first and this will open your billing details.";
    case "no_price":
      return "The subscription price is not set up yet. Nothing has been charged — please contact us.";
    case "price_unavailable":
      return "We could not read the price just now. Nothing has been charged — please try again.";
    case "bad_origin":
      return "We could not start checkout from this address. Please open the app from your usual link and try again.";
    case "stripe_error":
      return "Our payment provider could not start checkout. Nothing has been charged — please try again in a minute.";
    case "auth":
      return "Your session has expired. Sign in again and try once more.";
    default:
      return "Something went wrong starting checkout. Nothing has been charged — please try again.";
  }
}

function toBillingError(reason) {
  const e = new Error(billingReasonMessage(reason));
  e.code = reason || "unknown";
  return e;
}

/**
 * The company's billing state, read from the database.
 * @returns {Promise<{ state: 'trialing'|'active'|'read_only', trialEndsAt: string|null,
 *                     daysLeft: number|null, status: string|null, currentPeriodEnd: string|null,
 *                     hasCustomer: boolean }>}
 */
export async function getBillingState(companyId) {
  const c = client();
  const cid = companyId || (await getMyMembership())?.company_id || null;
  if (!cid) return { state: "trialing", trialEndsAt: null, daysLeft: null, status: null, currentPeriodEnd: null, hasCustomer: false };

  // The STATE comes from the database function, not from arithmetic here —
  // the same function can_write() gates on, so the banner and the gate can
  // never disagree about whether a company has lapsed.
  const { data: state, error: stateErr } = await c.rpc("company_billing_state", { p_company: cid });
  if (stateErr) throw stateErr;

  // The row is for DISPLAY only (how many days, is there a card on file).
  // A member with no select access, or no row at all, still gets a state.
  const { data: row } = await c
    .from("subscriptions")
    .select("status, trial_ends_at, current_period_end, stripe_customer_id")
    .eq("company_id", cid)
    .maybeSingle();

  const trialEndsAt = row?.trial_ends_at || null;
  let daysLeft = null;
  if (trialEndsAt) {
    const ms = new Date(trialEndsAt).getTime() - Date.now();
    // Ceiling, not floor: with 6 hours left a customer should read "1 day
    // left", not "0 days left" on a day they can still work.
    daysLeft = Math.max(0, Math.ceil(ms / 86400000));
  }

  return {
    state: state || "trialing",
    trialEndsAt,
    daysLeft,
    status: row?.status || null,
    currentPeriodEnd: row?.current_period_end || null,
    hasCustomer: !!row?.stripe_customer_id,
  };
}

async function invokeBilling(fn, method = "POST") {
  const c = client();
  const { data, error } = await c.functions.invoke(fn, { method });
  // A transport failure is not a refusal — say so differently.
  if (error) {
    const e = new Error("We could not reach our payment provider. Nothing has been charged — please try again.");
    e.code = "network"; e.cause = error;
    throw e;
  }
  if (!data || data.ok !== true) throw toBillingError(data && data.reason);
  return data;
}

/** The price, read from Stripe so no amount is ever hard-coded in this app. */
export async function getPrice() {
  try {
    const data = await invokeBilling("billing-checkout", "GET");
    return data.price || null;
  } catch {
    // The page must still render, with a Subscribe button and no amount,
    // rather than failing because a display detail could not be fetched.
    return null;
  }
}

/** Start Stripe Checkout. Returns the URL; the caller navigates. */
export async function startCheckout() {
  const data = await invokeBilling("billing-checkout", "POST");
  return data.url;
}

/** Open the Stripe customer portal. Returns the URL; the caller navigates. */
export async function openPortal() {
  const data = await invokeBilling("billing-portal", "POST");
  return data.url;
}
